import { recheckMcapIngest as requestMcapRecheck } from '@/features/mcap/api/submissions';
import { hashFileBlake3 } from '@/features/mcap/lib/blake3Client';
import { runMcapPreflight } from '@/features/mcap/lib/preflightClient';
import type { UploadState } from '@/features/mcap/lib/uploadMachine';
import {
  McapDuplicateAssetError,
  McapResumeAvailableError,
  uploadMcapFile,
} from '@/features/mcap/services/uploadService';
import { duplicateAssetProjectName } from '@/features/uploads/types/submissionConflicts';
import { ApiAuthError, ApiHttpError, isForbidden, isUnauthorized } from '@/shared/api/client';
import {
  MAX_DEDUP_FRAME_COUNT,
  MIN_DEDUP_FRAME_COUNT,
  checkContentHash,
  checkNearDuplicate,
  putSignedFrame,
  requestDedupFrameUrls,
} from '../api/dedup';
import { putGoogleStorageChunk } from '../api/googleStorage';
import { checkVideoQuality } from '../api/qualityCheck';
import {
  CHUNK_BYTES,
  LOCAL_UPLOAD_MAX_AUTO_RETRIES,
  MAX_CONCURRENT_UPLOADS,
  UPLOAD_RECONCILE_CONCURRENCY,
} from '../config/constants';
import { blake3File } from '../lib/blake3';
import { mintSessionId } from '../lib/ids';
import { sourceImportPatch } from '../lib/importProjection';
import {
  addLocalUploadTaskToIndex,
  indexLocalUploadTasks,
  localUploadIdentityKey,
} from '../lib/localUploadIdentity';
import { extractPdqFrames } from '../lib/pdqFrames';
import { buildQualityEvidence } from '../lib/qualityEvidence';
import {
  previousSourceImportFromConflict,
  sourceImportCreateConflict,
} from '../lib/sourceImportConflict';
import {
  UploadQueueLimitError,
  incompleteUploadCount,
  remainingUploadCapacity,
} from '../lib/uploadLimits';
import {
  hasUploadHttpStatus,
  resumableNotExpiredOffset,
  validateRestartResponse,
} from '../lib/uploadRestart';
import { planUploadResume } from '../lib/uploadResume';
import {
  type LocalUploadRetryEvent,
  isRetryableUploadError,
  runRetryableUploadOperation,
  waitForLocalUploadRetry,
} from '../lib/uploadRetry';
import { isVideoUploadFile } from '../lib/uploadSelection';
import { uploadFileHandleStore } from '../services/uploadFileHandleStore';
import { uploadRuntimeClient } from '../services/uploadRuntimeClient';
import { isLocalUploadLeader, requireLocalUploadLeader } from '../stores/uploadLeadershipStore';
import { uploadProgressActions } from '../stores/uploadProgressStore';
import { getActiveUploadUid, isTerminalPhase, uploadActions } from '../stores/uploadStore';
import type {
  LocalFileCandidate,
  LocalRecoveryOutcome,
  LocalRecoveryProgress,
  LocalRecoveryResult,
  LocalRecoverySummary,
} from '../types/uploadPreparation';
import type { LocalUploadRoute } from '../types/uploadRelay';
import type {
  DedupPrecheckOutcome,
  HistoryUploadResume,
  LocalUploadRetryOperation,
  ResumeUploadResult,
  StartSourceImportResult,
  Submission,
  UploadTarget,
  UploadTask,
} from '../types/uploads';
import {
  captureLocalUploadAutoRetryScheduled,
  captureLocalUploadCompleted,
  captureLocalUploadFailed,
  captureLocalUploadReattachCancelled,
  captureLocalUploadReattachCompleted,
  captureLocalUploadReattachProgress,
  captureLocalUploadReattachStarted,
  captureLocalUploadRetryRequested,
  captureLocalUploadStarted,
  captureUploadRestartClicked,
  captureUploadRestartCompletedFromLandedObject,
  captureUploadRestartCreated,
  captureUploadRestartFailed,
  captureUploadSessionExpiredDetected,
} from './localUploadAnalytics';
import { createSourceImport, getSourceImport } from './sourceImportService';
import {
  completeSubmission,
  createSubmission,
  getSubmission,
  getUploadOffset,
  reportProgress,
  restartExpiredSubmission,
  resumableBegin,
} from './submissionService';
import { createUploadRelayLease, uploadRelayFrames } from './uploadRelay';
import { withUploadResourceLock } from './uploadResourceLock';
import { uploadStatusCoordinator } from './uploadStatusCoordinator';

/**
 * The imperative upload/import engine. Owns the non-serializable per-item runtime (the `File`, the
 * live `XMLHttpRequest`, pause/cancel flags) in a module map and drives each item through the transfer
 * lifecycle. Durable phase/committed-byte state goes to the persisted transfer store; raw browser
 * progress is coalesced in the non-persisted progress store. It is deliberately NOT a TanStack Query
 * mutation: byte progress, pause, resume, and cancel need XHR. Uploads continue across route changes
 * because this module lives above the router.
 *
 * Direct upload (transport "B"): quality precheck → hash → exact-dup precheck → create →
 * resumable-begin → chunked XHR
 * PUT with `Content-Range` straight to the GCS resumable URI (real `upload.onprogress`), reporting each
 * chunk to the backend → complete (server `gcs_stat` authoritative; ambiguous GCS responses are
 * reconciled through the backend). Resume offset comes from `GET /submissions/:id/upload-offset`.
 * After the byte phase both channels hand off to the batch `uploadStatusCoordinator`, which reconciles every
 * in-flight transfer to a terminal outcome through two staggered, channel-scoped poll lanes (no
 * per-item polling).
 */

interface EngineEntry {
  relay?: ReturnType<typeof createUploadRelayLease>;
  file?: File; // absent after reload or a confirmed stall — the user must re-select it to resume
  xhr?: XMLHttpRequest;
  retryController?: AbortController;
  paused: boolean;
  cancelled: boolean;
  reattachGeneration?: number;
  reattachController?: AbortController;
  preparedContentBlake3?: string;
  needsQualityCheck?: boolean;
  restartCandidateId?: string;
  restarting?: boolean;
}

const entries = new Map<string, EngineEntry>();
let activeUploads = 0;
const uploadQueue: string[] = [];
let runtimeGeneration = 0;
let reconcileRun: { generation: number; promise: Promise<void> } | null = null;

type SourceImportCreateRequest = Parameters<typeof createSourceImport>[0];

const FILE_FREE_PHASES: ReadonlySet<UploadTask['phase']> = new Set([
  'processing',
  'accepted',
  'blocked-duplicate',
  'blocked-near-duplicate',
  'rejected-duplicate',
  'rejected-quality',
  'failed',
  'cancelled',
]);

function applyCreatedSourceImport(
  id: string,
  request: SourceImportCreateRequest,
  created: Awaited<ReturnType<typeof createSourceImport>>,
): void {
  if (
    created.importId !== request.importId ||
    created.projectId !== request.projectId ||
    created.sourceUrl !== request.sourceUrl
  ) {
    throw new Error('Existing import does not match this request');
  }
  set(id, {
    reason: undefined,
    statusSyncError: undefined,
    ...sourceImportPatch(created),
  });
  if (created.state === 'rejected' || created.state === 'failed') {
    entries.delete(id);
    uploadStatusCoordinator.untrack(id);
    return;
  }
  uploadStatusCoordinator.track(id, created.importId, created.mediaType);
}

async function createOrRecoverSourceImport(
  id: string,
  request: SourceImportCreateRequest,
): Promise<StartSourceImportResult['outcome']> {
  try {
    const created = await createSourceImport(request);
    applyCreatedSourceImport(id, request, created);
    return 'started';
  } catch (error) {
    const conflict = sourceImportCreateConflict(error);
    if (conflict === 'duplicate-source') {
      const previousSourceImport = previousSourceImportFromConflict(error);
      const previousFailed =
        previousSourceImport?.relocatedByOperationId == null &&
        (previousSourceImport?.mcapStatus === 'failed' ||
          previousSourceImport?.intakeState === 'failed');
      entries.delete(id);
      uploadStatusCoordinator.untrack(id);
      set(id, {
        backendId: undefined,
        phase: 'blocked-duplicate',
        previousSourceImport,
        reason: previousFailed ? (previousSourceImport?.failure ?? undefined) : undefined,
        statusSyncError: undefined,
      });
      return 'duplicate';
    }
    if (conflict === 'duplicate-id') {
      try {
        const existing = await getSourceImport(request.importId);
        applyCreatedSourceImport(id, request, existing);
        return 'started';
      } catch (recoveryError) {
        set(id, {
          phase: 'error',
          reason: describeError(recoveryError, 'Could not recover existing import'),
        });
        throw recoveryError;
      }
    }
    set(id, { phase: 'error', reason: describeError(error, 'Could not start import') });
    throw error;
  }
}

function emptyRecoveryCounts(): Record<LocalRecoveryOutcome, number> {
  return {
    new: 0,
    resumed: 0,
    alreadyUploading: 0,
    alreadyExists: 0,
    expired: 0,
    failed: 0,
    invalid: 0,
  };
}

/** Detach runtime handles before aborting them so a subsequent reattach can never inherit or abort
 * the previous run's XHR/controller. Each run retains its own controller reference for cleanup. */
function abortEntryRuntime(entry: EngineEntry): void {
  const xhr = entry.xhr;
  if (entry.xhr === xhr) entry.xhr = undefined;
  xhr?.abort();

  const controller = entry.retryController;
  if (entry.retryController === controller) entry.retryController = undefined;
  controller?.abort();
}

function abortReattach(entry: EngineEntry, reason: string): void {
  entry.reattachController?.abort(reason);
}

/** Stop in-memory work owned by the previous authenticated user. Persisted rows remain in that
user's UID-scoped store and can be reconciled after they sign in again. */
export function resetUploadRuntime(): void {
  runtimeGeneration += 1;
  reconcileRun = null;
  for (const entry of entries.values()) {
    entry.cancelled = true;
    abortReattach(entry, 'runtime_reset');
    abortEntryRuntime(entry);
  }
  entries.clear();
  uploadQueue.length = 0;
  uploadProgressActions.reset();
  uploadStatusCoordinator.reset();
}

function newId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

function set(id: string, patch: Partial<UploadTask>): void {
  if (!isLocalUploadLeader()) return;
  uploadActions.patch(id, patch);
  if (patch.bytes !== undefined || patch.phase !== undefined) uploadProgressActions.clear(id);
  const item = uploadActions.get(id);
  if (item?.channel === 'upload' && FILE_FREE_PHASES.has(item.phase)) {
    const entry = entries.get(id);
    if (entry) entry.file = undefined;
    uploadRuntimeClient.releaseFile(id);
    const uid = getActiveUploadUid();
    if (uid) void uploadFileHandleStore.remove(uid, id);
  }
}

function retainUploadFile(id: string, file: File): void {
  uploadRuntimeClient.retainFile(id, file);
}

function releaseRestartFile(id: string, entry: EngineEntry): void {
  entry.file = undefined;
  entry.preparedContentBlake3 = undefined;
  uploadRuntimeClient.releaseFile(id);
  const uid = getActiveUploadUid();
  if (uid) void uploadFileHandleStore.remove(uid, id);
}

function recordLocalUploadFailure(
  id: string,
  stage: Parameters<typeof captureLocalUploadFailed>[1],
  error: unknown,
): void {
  const item = uploadActions.get(id);
  if (item?.channel === 'upload') captureLocalUploadFailed(item, stage, error);
}

function recordAutomaticRetry(id: string, event: LocalUploadRetryEvent): void {
  const item = uploadActions.get(id);
  if (!item || item.channel !== 'upload') return;
  captureLocalUploadAutoRetryScheduled(item, event);
  set(id, {
    autoRetryCount: (item.autoRetryCount ?? 0) + 1,
    autoRetry: {
      operation: event.operation,
      attempt: event.attempt,
      maxRetries: event.maxRetries,
      delayMs: event.delayMs,
      waitingForNetwork: event.waitingForNetwork,
    },
  });
}

function settleAutomaticRetry(id: string): void {
  set(id, { autoRetry: undefined });
}

// ── Public API ─────────────────────────────────────────────────────────────────────────────────────

function enqueueUploadUnchecked(target: UploadTarget, file: File): string {
  const id = newId();
  const item: UploadTask = {
    id,
    createdAt: Date.now(),
    channel: 'upload',
    mediaType: 'video',
    target,
    displayName: file.name,
    phase: 'queued',
    bytes: 0,
    totalBytes: file.size,
  };
  uploadActions.add(item);
  captureLocalUploadStarted(item);
  entries.set(id, { file, paused: false, cancelled: false });
  retainUploadFile(id, file);
  uploadQueue.push(id);
  pump();
  return id;
}

/** Atomically add a local video batch without ever partially exceeding the unfinished-work limit. */
export function enqueueUploads(target: UploadTarget, files: readonly File[]): string[] {
  requireLocalUploadLeader();
  if (files.some((file) => !isVideoUploadFile(file))) {
    throw new Error('video_upload_requires_video_file');
  }
  const items = uploadActions.all();
  if (files.length > remainingUploadCapacity(items)) {
    throw new UploadQueueLimitError(incompleteUploadCount(items), files.length);
  }
  return files.map((file) => enqueueUploadUnchecked(target, file));
}

/** Add one local file for direct upload into `target`, returning the created transfer item id. */
export function enqueueUpload(target: UploadTarget, file: File): string {
  return enqueueUploads(target, [file])[0];
}

function enqueueMcapUploadUnchecked(target: UploadTarget, file: File): string {
  const id = newId();
  const item: UploadTask = {
    id,
    createdAt: Date.now(),
    channel: 'upload',
    mediaType: 'mcap',
    target,
    displayName: file.name,
    phase: 'queued',
    bytes: 0,
    totalBytes: file.size,
  };
  uploadActions.add(item);
  captureLocalUploadStarted(item);
  entries.set(id, { file, paused: false, cancelled: false });
  retainUploadFile(id, file);
  uploadQueue.push(id);
  pump();
  return id;
}

function recoveryItem(
  target: UploadTarget,
  file: File,
  mediaType: 'video' | 'mcap',
  session: Submission,
  contentBlake3: string,
  uploadRoute: LocalUploadRoute = 'direct',
): string {
  const id = newId();
  const item: UploadTask = {
    id,
    createdAt: Date.now(),
    channel: 'upload',
    ...(uploadRoute === 'china' ? { uploadRoute } : {}),
    mediaType,
    target,
    displayName: file.name,
    phase: 'queued',
    bytes: session.receivedBytes,
    totalBytes: session.declaredBytes,
    backendId: session.sessionId,
    contentBlake3,
  };
  uploadActions.add(item);
  entries.set(id, {
    file,
    paused: false,
    cancelled: false,
    preparedContentBlake3: contentBlake3,
    needsQualityCheck: mediaType === 'video',
  });
  retainUploadFile(id, file);
  uploadQueue.push(id);
  pump();
  return id;
}

function recoveryResumeItem(
  target: UploadTarget,
  file: File,
  mediaType: 'video' | 'mcap',
  session: Submission,
  contentBlake3: string,
  uploadRoute: LocalUploadRoute = 'direct',
): string {
  const item: UploadTask = {
    id: newId(),
    createdAt: Date.now(),
    channel: 'upload',
    ...(uploadRoute === 'china' ? { uploadRoute } : {}),
    mediaType,
    target,
    displayName: file.name,
    phase: 'paused',
    bytes: session.receivedBytes,
    totalBytes: session.declaredBytes,
    backendId: session.sessionId,
    contentBlake3,
    resumeAvailable: true,
    reason: 'Continue the existing upload',
  };
  uploadActions.add(item);
  entries.set(item.id, {
    file,
    paused: true,
    cancelled: false,
    preparedContentBlake3: contentBlake3,
  });
  retainUploadFile(item.id, file);
  return item.id;
}

function matchesRecoverySession(
  session: Submission,
  target: UploadTarget,
  mediaType: 'video' | 'mcap',
  file: File,
  contentBlake3: string,
): boolean {
  return (
    session.projectId === target.projectId &&
    session.contentBlake3 === contentBlake3 &&
    session.declaredBytes === file.size &&
    session.mediaType === mediaType
  );
}

function isExpiredUploadSessionError(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 410;
}

function markUploadSessionExpired(id: string): void {
  expireUploadSession(id);
}

function stageExistingUpload(
  item: UploadTask,
  file: File,
  contentBlake3: string,
  session?: Submission,
): string {
  const entry = entries.get(item.id) ?? { paused: true, cancelled: false };
  abortEntryRuntime(entry);
  entry.file = file;
  entry.paused = true;
  entry.cancelled = false;
  entry.preparedContentBlake3 = contentBlake3;
  entries.set(item.id, entry);
  retainUploadFile(item.id, file);
  set(item.id, {
    backendId: session?.sessionId ?? item.backendId,
    phase: 'paused',
    bytes: session?.receivedBytes ?? item.bytes,
    totalBytes: session?.declaredBytes ?? file.size,
    contentBlake3,
    needsReattach: undefined,
    needsRestart: undefined,
    resumeAvailable: true,
    reason: 'Continue the existing upload',
    statusSyncError: undefined,
  });
  return item.id;
}

function hasActiveLocalWork(item: UploadTask): boolean {
  if (item.phase === 'reattaching') return true;
  if (item.needsReattach || item.phase === 'paused' || isTerminalPhase(item.phase)) return false;
  const entry = entries.get(item.id);
  return Boolean(entry?.file && !entry.cancelled && !entry.paused);
}

function hasCompletedLocalUpload(item: UploadTask): boolean {
  return (
    item.phase === 'processing' ||
    item.phase === 'accepted' ||
    item.phase === 'blocked-duplicate' ||
    item.phase === 'blocked-near-duplicate' ||
    item.phase === 'rejected-duplicate' ||
    item.phase === 'rejected-quality'
  );
}

function isRecoveryBatchFailure(error: unknown): boolean {
  return error instanceof ApiAuthError || isUnauthorized(error) || isForbidden(error);
}

function resumeRecoveredUpload(id: string): void {
  const result = resumeUpload(id);
  if (result.ok) return;
  if (result.code === 'not_upload_leader') requireLocalUploadLeader();
  throw new Error(`automatic_resume_failed:${result.code}`);
}

/** Hash a freshly selected collection once, then independently create, skip, or resume each file.
 * Sequential hashing keeps a large folder from saturating the source disk before byte uploads start. */
export async function recoverLocalFiles(
  target: UploadTarget,
  candidates: readonly LocalFileCandidate[],
  onProgress?: (progress: LocalRecoveryProgress) => void,
  shouldContinue?: () => boolean,
  onTransferIdentified?: (candidate: LocalFileCandidate, uploadTaskId: string) => void,
  uploadRoute: LocalUploadRoute = 'direct',
): Promise<LocalRecoverySummary> {
  requireLocalUploadLeader();
  const counts = emptyRecoveryCounts();
  const results: LocalRecoveryResult[] = [];
  const taskIndex = indexLocalUploadTasks(uploadActions.all());
  const completedIdentityKeys = new Set<string>();
  let checkedFiles = 0;
  for (const [index, candidate] of candidates.entries()) {
    if (shouldContinue && !shouldContinue()) break;
    requireLocalUploadLeader();
    const { file } = candidate;
    const displayName = candidate.relativePath ?? file.name;
    const report = (
      stage: LocalRecoveryProgress['stage'],
      completedFiles = index,
      completedBytes = stage === 'checking' ? file.size : 0,
    ) =>
      onProgress?.({
        completedBytes,
        completedFiles,
        currentFileName: displayName,
        stage,
        totalBytes: file.size,
        totalFiles: candidates.length,
      });
    const identify = (uploadTaskId: string, identityKey: string): string => {
      onTransferIdentified?.(candidate, uploadTaskId);
      addLocalUploadTaskToIndex(taskIndex, identityKey, uploadTaskId);
      return uploadTaskId;
    };
    const record = (outcome: LocalRecoveryOutcome, contentBlake3?: string, reason?: string) => {
      results.push({
        candidateId: candidate.candidateId,
        contentBlake3,
        displayName,
        outcome,
        reason,
      });
      counts[outcome] += 1;
    };
    report('hashing');
    let contentBlake3: string | undefined;
    try {
      const mediaType = file.name.toLowerCase().endsWith('.mcap')
        ? ('mcap' as const)
        : isVideoUploadFile(file)
          ? ('video' as const)
          : undefined;
      if (!mediaType || file.size <= 0) {
        record('invalid', undefined, 'File type or size is invalid');
        continue;
      }
      contentBlake3 =
        mediaType === 'mcap'
          ? await hashFileBlake3(file, (progress) =>
              report('hashing', index, progress.completedBytes),
            )
          : await blake3File(file, (completedBytes) => report('hashing', index, completedBytes));
      requireLocalUploadLeader();
      report('checking');
      const identityKey = localUploadIdentityKey({
        projectId: target.projectId,
        mediaType,
        size: file.size,
        contentBlake3,
      });
      const localMatches = (taskIndex.get(identityKey) ?? [])
        .map((id) => uploadActions.get(id))
        .filter((item): item is UploadTask => item !== undefined);
      const activeLocal = localMatches.find(hasActiveLocalWork);
      if (activeLocal) {
        record('alreadyUploading', contentBlake3);
        continue;
      }

      const interruptedLocal =
        localMatches.find((item) => item.needsReattach || item.phase === 'paused') ??
        localMatches.find((item) => !hasCompletedLocalUpload(item));
      if (interruptedLocal?.backendId) {
        try {
          const session = await getSubmission(interruptedLocal.backendId);
          requireLocalUploadLeader();
          if (!matchesRecoverySession(session, target, mediaType, file, contentBlake3)) {
            record('invalid', contentBlake3, 'Existing upload session identity does not match');
            continue;
          }
          if (session.status === 'uploaded') {
            set(interruptedLocal.id, {
              phase: 'processing',
              bytes: session.declaredBytes,
              totalBytes: session.declaredBytes,
              needsReattach: undefined,
              resumeAvailable: undefined,
              reason: undefined,
            });
            uploadStatusCoordinator.track(interruptedLocal.id, session.sessionId, mediaType);
            completedIdentityKeys.add(identityKey);
            record('alreadyExists', contentBlake3);
            continue;
          }
          if (session.status === 'failed') {
            set(interruptedLocal.id, {
              phase: 'failed',
              reason: session.failReason ?? 'This upload session has failed',
              needsReattach: undefined,
              resumeAvailable: undefined,
            });
            record('failed', contentBlake3, session.failReason ?? undefined);
            continue;
          }
          if (session.status === 'abandoned') {
            record('expired', contentBlake3, 'Upload was restarted in another session');
            continue;
          }
          if (interruptedLocal.needsRestart) {
            const uploadTaskId = identify(
              stageExistingUpload(interruptedLocal, file, contentBlake3, session),
              identityKey,
            );
            set(uploadTaskId, {
              phase: 'paused',
              needsRestart: true,
              reason: 'Upload session expired',
              resumeAvailable: undefined,
            });
            record('expired', contentBlake3, 'Restart the expired upload explicitly');
            continue;
          }
          const uploadTaskId = identify(
            stageExistingUpload(interruptedLocal, file, contentBlake3, session),
            identityKey,
          );
          resumeRecoveredUpload(uploadTaskId);
          record('resumed', contentBlake3);
          continue;
        } catch (error) {
          if (isExpiredUploadSessionError(error)) {
            markUploadSessionExpired(interruptedLocal.id);
            record('expired', contentBlake3, 'Upload session expired');
            continue;
          }
          throw error;
        }
      }

      if (completedIdentityKeys.has(identityKey) || localMatches.some(hasCompletedLocalUpload)) {
        completedIdentityKeys.add(identityKey);
        record('alreadyExists', contentBlake3);
        continue;
      }

      const outcome = await createSubmission({
        sessionId: mintSessionId(contentBlake3),
        projectId: target.projectId,
        contentBlake3,
        mediaType,
        originalFilename: file.name,
        declaredBytes: file.size,
      });
      requireLocalUploadLeader();
      if (outcome.kind === 'created') {
        if (!matchesRecoverySession(outcome.submission, target, mediaType, file, contentBlake3)) {
          record('failed', contentBlake3, 'Created upload session identity does not match');
          continue;
        }
        if (interruptedLocal) {
          const uploadTaskId = identify(
            stageExistingUpload(interruptedLocal, file, contentBlake3, outcome.submission),
            identityKey,
          );
          resumeRecoveredUpload(uploadTaskId);
          record('resumed', contentBlake3);
        } else {
          identify(
            recoveryItem(target, file, mediaType, outcome.submission, contentBlake3, uploadRoute),
            identityKey,
          );
          record('new', contentBlake3);
        }
        continue;
      }
      if (outcome.kind === 'duplicate-asset') {
        if (interruptedLocal) {
          const projectName = duplicateAssetProjectName(outcome);
          set(interruptedLocal.id, {
            contentBlake3,
            phase: 'blocked-duplicate',
            reason: projectName
              ? `Already exists in project “${projectName}”`
              : 'This file already exists',
            needsReattach: undefined,
            resumeAvailable: undefined,
          });
        }
        completedIdentityKeys.add(identityKey);
        record('alreadyExists', contentBlake3);
        continue;
      }
      let session: Submission;
      try {
        session = await getSubmission(outcome.sessionId);
        requireLocalUploadLeader();
      } catch (error) {
        if (isExpiredUploadSessionError(error)) {
          record('expired', contentBlake3, 'Upload session expired');
          continue;
        }
        throw error;
      }
      if (!matchesRecoverySession(session, target, mediaType, file, contentBlake3)) {
        record('invalid', contentBlake3, 'Existing upload session identity does not match');
        continue;
      }
      if (session.status === 'uploaded') {
        completedIdentityKeys.add(identityKey);
        record('alreadyExists', contentBlake3);
        continue;
      }
      if (session.status === 'failed') {
        record('failed', contentBlake3, session.failReason ?? undefined);
        continue;
      }
      if (session.status === 'abandoned') {
        record('expired', contentBlake3, 'Upload was restarted in another session');
        continue;
      }
      const trackedUpload = uploadActions
        .all()
        .find((item) => item.channel === 'upload' && item.backendId === session.sessionId);
      if (trackedUpload && hasActiveLocalWork(trackedUpload)) {
        addLocalUploadTaskToIndex(taskIndex, identityKey, trackedUpload.id);
        record('alreadyUploading', contentBlake3);
        continue;
      }
      const resumableTask = trackedUpload ?? interruptedLocal;
      const uploadTaskId = identify(
        resumableTask
          ? stageExistingUpload(resumableTask, file, contentBlake3, session)
          : recoveryResumeItem(target, file, mediaType, session, contentBlake3, uploadRoute),
        identityKey,
      );
      resumeRecoveredUpload(uploadTaskId);
      record('resumed', contentBlake3);
    } catch (error) {
      if (!isLocalUploadLeader()) requireLocalUploadLeader();
      if (isRecoveryBatchFailure(error)) throw error;
      record('failed', contentBlake3, describeError(error, 'Could not prepare this file'));
    } finally {
      checkedFiles = index + 1;
      report('checking', index + 1, 0);
    }
  }
  if (isLocalUploadLeader()) pump();
  return {
    results,
    counts,
    checkedFiles,
    stopped: checkedFiles < candidates.length,
    totalFiles: candidates.length,
  };
}

/** Add one or more MCAP recordings to the same durable, bounded-concurrency queue used by videos and
 * source imports. Each recording becomes its own transfer item and backend session; the shared `pump()`
 * scheduler caps how many hash/upload at once. Validation is all-or-nothing so a bad file starts none. */
export function enqueueMcapUploads(target: UploadTarget, files: readonly File[]): string[] {
  requireLocalUploadLeader();
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.mcap') || file.size <= 0) {
      throw new Error('mcap_upload_requires_nonempty_mcap_file');
    }
  }
  return files.map((file) => enqueueMcapUploadUnchecked(target, file));
}

/** Add one MCAP recording to the durable queue, returning the created transfer item id. */
export function enqueueMcapUpload(target: UploadTarget, file: File): string {
  return enqueueMcapUploads(target, [file])[0];
}

/** Persist the import row before POST so fast completion and ambiguous responses remain visible. */
export async function startSourceImport(
  target: UploadTarget,
  importId: string,
  sourceUrl: string,
  displayName: string,
): Promise<StartSourceImportResult> {
  requireLocalUploadLeader();
  const id = newId();
  const item: UploadTask = {
    id,
    createdAt: Date.now(),
    channel: 'import',
    target,
    displayName,
    phase: 'creating',
    bytes: 0,
    totalBytes: 0,
    backendId: importId,
    sourceUrl,
  };
  uploadActions.add(item);
  entries.set(id, { paused: false, cancelled: false });
  const request = { importId, projectId: target.projectId, sourceUrl };
  const outcome = await createOrRecoverSourceImport(id, request);
  return { id, outcome };
}

/** Add an already-created GCS-folder import to the same status coordinator as direct source imports. */
export function trackGcsFolderSourceImport(
  target: UploadTarget,
  importId: string,
  sourceUrl: string,
  displayName: string,
  totalBytes: number,
): string {
  requireLocalUploadLeader();
  const existing = uploadActions
    .all()
    .find((item) => item.channel === 'import' && item.backendId === importId);
  if (existing) {
    if (existing.totalBytes === 0 && totalBytes > 0) {
      uploadActions.patch(existing.id, { totalBytes });
    }
    if (!isTerminalPhase(existing.phase))
      uploadStatusCoordinator.track(existing.id, importId, 'mcap');
    return existing.id;
  }
  const id = newId();
  uploadActions.add({
    id,
    createdAt: Date.now(),
    channel: 'import',
    mediaType: 'mcap',
    target,
    displayName,
    phase: 'creating',
    bytes: 0,
    totalBytes,
    backendId: importId,
    sourceUrl,
  });
  entries.set(id, { paused: false, cancelled: false });
  uploadStatusCoordinator.track(id, importId, 'mcap');
  return id;
}

export function pauseUpload(id: string): void {
  if (!isLocalUploadLeader()) return;
  const e = entries.get(id);
  if (!e) return;
  const wasReattaching = uploadActions.get(id)?.phase === 'reattaching';
  e.paused = true;
  abortReattach(e, 'paused');
  abortEntryRuntime(e);
  set(id, {
    phase: 'paused',
    autoRetry: undefined,
    needsReattach: wasReattaching ? true : undefined,
  });
}

/** Stop a live upload whose browser progress and authoritative GCS offset both remained stalled.
 * The backend session stays resumable: this deliberately does not call `failSubmission`. */
export function interruptStalledUpload(id: string): void {
  const item = uploadActions.get(id);
  if (
    !item ||
    item.channel !== 'upload' ||
    item.phase !== 'uploading' ||
    !item.backendId ||
    item.needsReattach
  ) {
    return;
  }
  const entry = entries.get(id);
  if (entry) {
    entry.paused = true;
    abortEntryRuntime(entry);
    entry.file = undefined;
  }
  set(id, {
    phase: 'paused',
    needsReattach: true,
    speedBps: undefined,
    autoRetry: undefined,
  });
}

/** Stop the old writer after the backend proves its GCS capability expired. A retained File can be
 * verified and restarted; otherwise the user must select the original file. */
export function expireUploadSession(id: string): void {
  const item = uploadActions.get(id);
  if (
    !item ||
    item.channel !== 'upload' ||
    !item.backendId ||
    item.needsRestart ||
    item.phase === 'processing' ||
    item.phase === 'accepted' ||
    item.phase === 'cancelled' ||
    item.phase.startsWith('rejected-') ||
    item.phase.startsWith('blocked-')
  ) {
    return;
  }
  const entry = entries.get(id);
  if (entry) {
    entry.paused = true;
    abortReattach(entry, 'session_expired');
    abortEntryRuntime(entry);
  }
  captureUploadSessionExpiredDetected(item);
  set(id, {
    phase: 'paused',
    reason: 'Upload session expired',
    needsRestart: true,
    needsReattach: entry?.file ? undefined : true,
    resumeAvailable: undefined,
    speedBps: undefined,
    autoRetry: undefined,
  });
}

export function resumeUpload(id: string): ResumeUploadResult {
  if (!isLocalUploadLeader()) return { ok: false, code: 'not_upload_leader' };
  const item = uploadActions.get(id);
  if (!item) return { ok: false, code: 'transfer_unavailable' };
  const e = entries.get(id);
  if (!e?.file) {
    set(id, {
      phase: 'paused',
      needsReattach: true,
      resumeAvailable: undefined,
      reason: undefined,
    });
    return { ok: false, code: 'file_required' };
  }
  const alreadyQueued = uploadQueue.includes(id);
  if (!alreadyQueued && !e.paused && item.phase !== 'paused') {
    return { ok: true, status: 'already_queued' };
  }
  e.paused = false;
  e.cancelled = false;
  e.retryController = undefined;
  if (!alreadyQueued) uploadQueue.push(id);
  set(id, { phase: 'queued', resumeAvailable: undefined });
  pump();
  return { ok: true, status: alreadyQueued ? 'already_queued' : 'queued' };
}

export function cancelUploadTask(id: string): void {
  if (!isLocalUploadLeader()) return;
  const e = entries.get(id);
  if (e) {
    e.cancelled = true;
    abortReattach(e, 'cancelled');
    abortEntryRuntime(e);
  }
  uploadStatusCoordinator.untrack(id);
  set(id, { phase: 'cancelled' });
}

export function retryUploadTask(id: string): void {
  if (!isLocalUploadLeader()) return;
  const item = uploadActions.get(id);
  if (!item) return;
  if (item.channel === 'import' && item.backendId && item.sourceUrl) {
    if (!entries.has(id)) entries.set(id, { paused: false, cancelled: false });
    set(id, { phase: 'creating', reason: undefined, statusSyncError: undefined });
    void createOrRecoverSourceImport(id, {
      importId: item.backendId,
      projectId: item.target.projectId,
      sourceUrl: item.sourceUrl,
    }).catch(() => {});
    return;
  }
  const e = entries.get(id);
  if (!e) return;
  if (item.channel === 'upload' && item.mediaType === 'mcap' && e.file) {
    captureLocalUploadRetryRequested(item);
    e.cancelled = false;
    set(id, {
      phase: 'queued',
      reason: undefined,
      autoRetry: undefined,
      autoRetryCount: undefined,
    });
    if (!uploadQueue.includes(id)) uploadQueue.push(id);
    pump();
  } else if (item.channel === 'upload' && e.file) {
    captureLocalUploadRetryRequested(item);
    e.cancelled = false;
    e.paused = false;
    set(id, {
      phase: 'queued',
      reason: undefined,
      autoRetry: undefined,
      autoRetryCount: undefined,
    });
    if (!uploadQueue.includes(id)) uploadQueue.push(id);
    pump();
  }
}

/**
 * Re-attach a file after a page reload or confirmed transport stall. The queue retains only
 * serializable metadata, so a `needsReattach` upload requires the user to re-pick the original file.
 * Hash and backend session identity are verified before the selection is staged. A successful match
 * is queued immediately, so one file selection is enough to continue from the authoritative offset.
 */
export async function reattachFile(
  id: string,
  file: File,
  options: { resume?: boolean } = {},
): Promise<void> {
  if (!isLocalUploadLeader()) return;
  const item = uploadActions.get(id);
  if (!item || item.channel !== 'upload') return;
  const fileTypeMatches =
    item.mediaType === 'mcap' ? file.name.toLowerCase().endsWith('.mcap') : isVideoUploadFile(file);
  if (!fileTypeMatches) {
    recordLocalUploadFailure(id, item.phase, new Error('reattach_file_type_mismatch'));
    set(id, {
      phase: item.needsRestart ? 'paused' : 'error',
      reason: item.needsRestart
        ? 'restart_file_mismatch'
        : item.mediaType === 'mcap'
          ? 'Choose the original MCAP file to resume this upload'
          : 'Choose the original video file to resume this upload',
      needsReattach: true,
    });
    return;
  }
  if (file.size !== item.totalBytes) {
    recordLocalUploadFailure(id, item.phase, new Error('reattach_file_size_mismatch'));
    set(id, {
      phase: item.needsRestart ? 'paused' : 'error',
      reason: item.needsRestart
        ? 'restart_file_mismatch'
        : 'Selected file size does not match the original upload',
      needsReattach: true,
    });
    return;
  }
  const entry = entries.get(id) ?? { paused: true, cancelled: false };
  retainUploadFile(id, file);
  abortReattach(entry, 'replaced');
  const generation = (entry.reattachGeneration ?? 0) + 1;
  entry.reattachGeneration = generation;
  const controller = new AbortController();
  entry.reattachController = controller;
  entries.set(id, entry);
  const isCurrent = () =>
    entries.get(id)?.reattachGeneration === generation &&
    entries.get(id)?.reattachController === controller;
  const startedAt = Date.now();
  let lastProgressMilestone = 0;

  set(id, {
    phase: 'reattaching',
    needsReattach: undefined,
    resumeAvailable: undefined,
    reason: undefined,
  });
  uploadProgressActions.report(id, {
    stage: 'verification',
    bytes: 0,
    totalBytes: file.size,
  });
  captureLocalUploadReattachStarted(item);

  try {
    const mediaType = item.mediaType ?? 'video';
    const contentBlake3 = await hashFileBlake3(
      file,
      ({ completedBytes, totalBytes }) => {
        if (!isCurrent()) return;
        uploadProgressActions.report(id, {
          stage: 'verification',
          bytes: completedBytes,
          totalBytes,
        });
        const percent = totalBytes > 0 ? Math.floor((completedBytes / totalBytes) * 100) : 100;
        for (const milestone of [25, 50, 75, 100]) {
          if (milestone > lastProgressMilestone && percent >= milestone) {
            lastProgressMilestone = milestone;
            captureLocalUploadReattachProgress(uploadActions.get(id) ?? item, milestone);
          }
        }
      },
      controller.signal,
    );
    if (!isCurrent()) return;
    if (item.contentBlake3 && contentBlake3 !== item.contentBlake3) {
      if (item.needsRestart) releaseRestartFile(id, entry);
      recordLocalUploadFailure(id, item.phase, new Error('reattach_file_content_mismatch'));
      set(id, {
        phase: item.needsRestart ? 'paused' : 'error',
        reason: item.needsRestart
          ? 'restart_file_mismatch'
          : 'Selected file content does not match the original upload',
        needsReattach: true,
      });
      return;
    }

    if (item.needsRestart) {
      entry.file = file;
      entry.preparedContentBlake3 = contentBlake3;
      entry.paused = true;
      set(id, {
        phase: 'paused',
        contentBlake3,
        needsReattach: undefined,
        reason: 'Upload session expired',
      });
      captureLocalUploadReattachCompleted(
        uploadActions.get(id) ?? item,
        Date.now() - startedAt,
        false,
      );
      if (options.resume !== false) void restartExpiredUpload(id);
      return;
    }

    if (!item.backendId) {
      stageExistingUpload(item, file, contentBlake3);
      const resumed = options.resume === false ? undefined : resumeUpload(id);
      captureLocalUploadReattachCompleted(
        uploadActions.get(id) ?? item,
        Date.now() - startedAt,
        resumed?.ok ?? false,
      );
      return;
    }

    const session = await runDirectRetryableOperation(
      id,
      'resolve_submission',
      controller.signal,
      () => getSubmission(item.backendId as string),
    );
    if (!isCurrent()) return;
    if (!matchesRecoverySession(session, item.target, mediaType, file, contentBlake3)) {
      recordLocalUploadFailure(id, item.phase, new Error('reattach_session_mismatch'));
      set(id, {
        phase: 'error',
        reason: 'Selected file does not match the original upload session',
        needsReattach: true,
      });
      return;
    }
    if (session.status === 'uploaded') {
      entry.file = undefined;
      set(id, {
        phase: 'processing',
        bytes: session.declaredBytes,
        totalBytes: session.declaredBytes,
        contentBlake3,
        needsReattach: undefined,
        resumeAvailable: undefined,
        reason: undefined,
      });
      uploadStatusCoordinator.track(id, session.sessionId, mediaType);
      captureLocalUploadReattachCompleted(
        uploadActions.get(id) ?? item,
        Date.now() - startedAt,
        false,
      );
      return;
    }
    if (session.status === 'failed') {
      entry.file = undefined;
      set(id, {
        phase: 'failed',
        reason: session.failReason ?? 'This upload session has failed',
        needsReattach: undefined,
        resumeAvailable: undefined,
      });
      captureLocalUploadReattachCompleted(
        uploadActions.get(id) ?? item,
        Date.now() - startedAt,
        false,
      );
      return;
    }
    stageExistingUpload(item, file, contentBlake3, session);
    const resumed = options.resume === false ? undefined : resumeUpload(id);
    captureLocalUploadReattachCompleted(
      uploadActions.get(id) ?? item,
      Date.now() - startedAt,
      resumed?.ok ?? false,
    );
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError' || controller.signal.aborted) {
      const reason =
        typeof controller.signal.reason === 'string' ? controller.signal.reason : 'aborted';
      captureLocalUploadReattachCancelled(item, Date.now() - startedAt, reason);
      if (isCurrent() && !entry.cancelled) {
        set(id, {
          phase: 'paused',
          needsReattach: true,
          resumeAvailable: undefined,
          reason: item.needsRestart ? 'restart_failed' : undefined,
        });
      }
      return;
    }
    if (!isCurrent()) return;
    if (isExpiredUploadSessionError(error)) {
      markUploadSessionExpired(id);
      return;
    }
    if (item.needsRestart) releaseRestartFile(id, entry);
    recordLocalUploadFailure(id, item.phase, error);
    set(id, {
      phase: item.needsRestart ? 'paused' : 'error',
      reason: item.needsRestart
        ? 'restart_failed'
        : describeError(error, 'Could not verify the selected file'),
      needsReattach: true,
    });
  } finally {
    if (entry.reattachController === controller) entry.reattachController = undefined;
  }
}

/** Promote an interrupted history-only submission into the live upload queue, then reuse the same
 * verified staging path as a page-rehydrated row. The backend session is kept live throughout. */
export function reattachHistoricalUpload(upload: HistoryUploadResume, file: File): string {
  requireLocalUploadLeader();
  const tracked = uploadActions
    .all()
    .find((item) => item.channel === 'upload' && item.backendId === upload.sessionId);
  const id = tracked?.id ?? newId();
  if (!tracked) {
    uploadActions.add({
      id,
      createdAt: Date.now(),
      channel: 'upload',
      mediaType: upload.mediaType,
      target: { organizationId: '', projectId: upload.projectId },
      displayName: upload.displayName,
      phase: 'paused',
      bytes: upload.receivedBytes,
      totalBytes: upload.declaredBytes,
      backendId: upload.sessionId,
      contentBlake3: upload.contentBlake3,
      needsReattach: true,
    });
  }
  void reattachFile(id, file);
  return id;
}

/** A history-only expired session becomes a local task only after the user selects a File. */
export function restartHistoricalUpload(upload: HistoryUploadResume, file: File): string {
  requireLocalUploadLeader();
  const tracked = uploadActions
    .all()
    .find((item) => item.channel === 'upload' && item.backendId === upload.sessionId);
  const id = tracked?.id ?? newId();
  if (!tracked) {
    uploadActions.add({
      id,
      createdAt: Date.now(),
      channel: 'upload',
      mediaType: upload.mediaType,
      target: { organizationId: '', projectId: upload.projectId },
      displayName: upload.displayName,
      phase: 'paused',
      bytes: upload.receivedBytes,
      totalBytes: upload.declaredBytes,
      backendId: upload.sessionId,
      contentBlake3: upload.contentBlake3,
      needsRestart: true,
      needsReattach: true,
    });
  } else {
    set(id, {
      phase: 'paused',
      needsRestart: true,
      needsReattach: true,
      statusSyncError: undefined,
    });
  }
  void reattachFile(id, file);
  return id;
}

/** Explicitly restart one expired session, after verifying the same original File. The backend
 * atomically abandons the old row only if its URI is expired and no object has landed. */
export async function restartExpiredUpload(id: string): Promise<void> {
  if (!isLocalUploadLeader()) return;
  const item = uploadActions.get(id);
  const entry = entries.get(id);
  if (!item || item.channel !== 'upload' || !item.needsRestart || !item.backendId) return;
  if (!entry?.file) {
    set(id, { phase: 'paused', needsReattach: true });
    return;
  }
  if (entry.restarting) return;

  const file = entry.file;
  const previousSessionId = item.backendId;
  const generation = runtimeGeneration;
  const controller = new AbortController();
  entry.retryController = controller;
  entry.restarting = true;
  entry.paused = true;
  const isCurrent = () =>
    generation === runtimeGeneration &&
    isLocalUploadLeader() &&
    !entry.cancelled &&
    !controller.signal.aborted &&
    entries.get(id) === entry &&
    uploadActions.get(id)?.backendId === previousSessionId &&
    uploadActions.get(id)?.needsRestart === true;

  captureUploadRestartClicked(item);
  try {
    const typeMatches =
      item.mediaType === 'mcap'
        ? file.name.toLowerCase().endsWith('.mcap')
        : isVideoUploadFile(file);
    if (!typeMatches) throw new Error('restart_file_type_mismatch');
    if (file.size !== item.totalBytes) throw new Error('restart_file_size_mismatch');

    set(id, { phase: 'reattaching', reason: undefined });
    const contentBlake3 =
      entry.preparedContentBlake3 ??
      (await hashFileBlake3(
        file,
        ({ completedBytes, totalBytes }) => {
          if (isCurrent()) {
            uploadProgressActions.report(id, {
              stage: 'verification',
              bytes: completedBytes,
              totalBytes,
            });
          }
        },
        controller.signal,
      ));
    if (!isCurrent()) return;
    if (item.contentBlake3 && item.contentBlake3 !== contentBlake3) {
      throw new Error('restart_file_content_mismatch');
    }
    entry.preparedContentBlake3 = contentBlake3;
    set(id, { contentBlake3, phase: 'creating' });

    const replacementCandidateId = entry.restartCandidateId ?? mintSessionId(contentBlake3);
    entry.restartCandidateId = replacementCandidateId;
    const result = await restartExpiredSubmission(previousSessionId, replacementCandidateId, {
      signal: controller.signal,
    });
    if (!isCurrent()) return;
    validateRestartResponse(item, contentBlake3, previousSessionId, replacementCandidateId, result);

    entry.restartCandidateId = undefined;
    if (result.action === 'completed') {
      set(id, {
        phase: 'processing',
        backendId: previousSessionId,
        bytes: result.session.declaredBytes,
        totalBytes: result.session.declaredBytes,
        reason: undefined,
        needsRestart: undefined,
        needsReattach: undefined,
      });
      captureUploadRestartCompletedFromLandedObject(item);
      uploadStatusCoordinator.track(id, previousSessionId, item.mediaType);
      return;
    }

    const replacementId = result.session.sessionId;
    captureUploadRestartCreated(item, result.action);
    if (result.session.status === 'uploaded') {
      set(id, {
        backendId: replacementId,
        phase: 'processing',
        bytes: result.session.declaredBytes,
        totalBytes: result.session.declaredBytes,
        reason: undefined,
        needsRestart: undefined,
        needsReattach: undefined,
      });
      uploadStatusCoordinator.track(id, replacementId, item.mediaType);
      return;
    }

    const anotherWriter =
      result.action === 'existing_replacement' && replacementId !== replacementCandidateId;
    set(id, {
      backendId: replacementId,
      phase: 'paused',
      bytes: result.session.receivedBytes,
      totalBytes: result.session.declaredBytes,
      reason: anotherWriter ? 'Upload restarted in another browser' : undefined,
      needsRestart: undefined,
      needsReattach: anotherWriter ? true : undefined,
      speedBps: undefined,
    });
    if (anotherWriter) {
      releaseRestartFile(id, entry);
      return;
    }
    entry.paused = true;
    resumeUpload(id);
  } catch (error) {
    if (!isCurrent()) return;
    const offset = resumableNotExpiredOffset(error, file.size);
    if (offset !== undefined) {
      entry.restartCandidateId = undefined;
      set(id, {
        phase: 'paused',
        bytes: offset,
        reason: undefined,
        needsRestart: undefined,
        needsReattach: undefined,
      });
      entry.paused = true;
      resumeUpload(id);
      return;
    }

    captureUploadRestartFailed(item, error);
    const invalidFile = error instanceof Error && error.message.startsWith('restart_file_');
    if (invalidFile) {
      releaseRestartFile(id, entry);
    }
    set(id, {
      phase: 'paused',
      reason: invalidFile ? 'restart_file_mismatch' : 'restart_failed',
      needsRestart: true,
      needsReattach: invalidFile ? true : undefined,
    });
  } finally {
    entry.restarting = false;
    if (entry.retryController === controller) entry.retryController = undefined;
  }
}

function uploadTaskPatchFromMcapState(state: UploadState): Partial<UploadTask> {
  const phaseByMcapState: Record<UploadState['phase'], UploadTask['phase']> = {
    idle: 'queued',
    hashing: 'hashing',
    ready: 'creating',
    initiating: 'creating',
    opening_resumable: 'creating',
    uploading: 'uploading',
    finalizing: 'completing',
    processing: 'processing',
    accepted: 'accepted',
    paused_retryable: 'error',
    rejected: 'rejected-quality',
    failed: 'failed',
    canceled: 'cancelled',
  };
  const totalBytes = Number(state.totalBytes);
  const bytes =
    state.phase === 'hashing'
      ? Math.round(totalBytes * state.hashProgress)
      : Number(state.completedBytes);
  return {
    phase: phaseByMcapState[state.phase],
    backendId: state.sessionId,
    assetId: state.assetId,
    totalBytes,
    bytes,
    reason: state.errorCode,
  };
}

async function runMcapUpload(id: string): Promise<void> {
  const entry = entries.get(id);
  const item = uploadActions.get(id);
  if (!entry?.file || !item) return;
  const controller = new AbortController();
  entry.retryController = controller;
  const resumed = Boolean(item.backendId);
  let failureStage: UploadTask['phase'] = item.phase;
  const preflight = await runMcapPreflight(entry.file);
  if (entry.cancelled || entry.paused || controller.signal.aborted) {
    if (entry.retryController === controller) entry.retryController = undefined;
    return;
  }
  if (preflight.status === 'unsupported-encrypted-chunk') {
    // Match the backend's deterministic device-format failure before creating a submission or
    // transmitting the file. All other preflight outcomes remain advisory.
    entry.file = undefined;
    set(id, {
      phase: 'failed',
      reason: 'mcap_device_format_unsupported',
      mcapPreflightAdvisory: undefined,
      autoRetry: undefined,
    });
    if (entry.retryController === controller) entry.retryController = undefined;
    return;
  }
  // Preflight is advisory only. The bounded local descriptor read cannot prove a recording is
  // unusable, so an `invalid` result records a warning and the upload still proceeds — backend
  // intake re-validates the canonical bytes and is authoritative. (Previously `invalid` hard-blocked
  // the upload, which wrongly rejected files the backend would accept, e.g. a non-standard footer.)
  set(id, {
    mcapDescriptorWarnings:
      preflight.status === 'repairable'
        ? preflight.warnings.map(({ schemaName, trailingBytes }) => ({ schemaName, trailingBytes }))
        : undefined,
    mcapPreflightAdvisory: preflight.status === 'invalid' ? preflight.reason : undefined,
  });
  try {
    const result = await uploadMcapFile({
      uploadRoute: item.uploadRoute,
      projectId: item.target.projectId,
      file: entry.file,
      uploaderId: getActiveUploadUid() ?? undefined,
      resumeSessionId: item.backendId,
      rejectDuplicate: !item.backendId,
      preparedContentBlake3: item.contentBlake3,
      signal: controller.signal,
      onState: (state) => {
        const patch = uploadTaskPatchFromMcapState(state);
        if (patch.phase && patch.phase !== 'error') failureStage = patch.phase;
        if (state.phase === 'hashing') {
          const current = uploadActions.get(id);
          if (current?.phase !== 'hashing' || current.totalBytes !== patch.totalBytes) {
            set(id, { phase: 'hashing', bytes: 0, totalBytes: patch.totalBytes });
          }
          uploadProgressActions.report(id, {
            stage: 'verification',
            bytes: patch.bytes ?? 0,
            totalBytes: patch.totalBytes ?? entry.file?.size ?? item.totalBytes,
          });
          return;
        }
        set(id, { ...patch, autoRetry: undefined });
      },
      onRetry: (event) => recordAutomaticRetry(id, event),
    });
    const completed = uploadActions.get(id);
    if (completed) captureLocalUploadCompleted(completed, resumed);
    set(id, {
      backendId: result.sessionId,
      phase: 'processing',
      reason: undefined,
      autoRetry: undefined,
    });
    uploadStatusCoordinator.track(id, result.sessionId, 'mcap');
  } catch (error) {
    if (entry.cancelled || controller.signal.aborted) return;
    if (hasUploadHttpStatus(error, 410)) {
      expireUploadSession(id);
      return;
    }
    if (error instanceof McapDuplicateAssetError) {
      const projectName = duplicateAssetProjectName(error.conflict);
      set(id, {
        backendId: undefined,
        phase: 'blocked-duplicate',
        reason: projectName
          ? `Already exists in project “${projectName}”`
          : 'This file already exists',
        autoRetry: undefined,
      });
      return;
    }
    if (error instanceof McapResumeAvailableError) {
      entry.paused = true;
      set(id, {
        backendId: error.session.sessionId,
        contentBlake3: error.contentBlake3,
        phase: 'paused',
        bytes: Number(error.session.receivedBytes),
        totalBytes: Number(error.session.declaredBytes),
        resumeAvailable: true,
        reason: 'Continue the existing upload',
        autoRetry: undefined,
      });
      return;
    }
    const code = error instanceof Error ? error.message : 'upload_failed';
    if (code === 'duplicate_submission') {
      set(id, {
        phase: 'blocked-duplicate',
        reason: 'A submission for this file already exists',
        autoRetry: undefined,
      });
    } else if (code === 'resume_session_failed') {
      set(id, {
        phase: 'failed',
        reason: 'This upload session has failed',
        autoRetry: undefined,
      });
    } else {
      recordLocalUploadFailure(id, failureStage, error);
      set(id, { phase: 'error', reason: code, autoRetry: undefined });
    }
  } finally {
    if (entry.retryController === controller) entry.retryController = undefined;
  }
}

// ── Scheduler ──────────────────────────────────────────────────────────────────────────────────────

function pump(): void {
  while (activeUploads < MAX_CONCURRENT_UPLOADS && uploadQueue.length > 0) {
    const id = uploadQueue.shift();
    if (!id) break;
    const e = entries.get(id);
    if (!e || e.cancelled || e.paused || !e.file) continue;
    const item = uploadActions.get(id);
    const run = item?.mediaType === 'mcap' ? runMcapUpload : runUpload;
    activeUploads += 1;
    void run(id).finally(() => {
      activeUploads -= 1;
      pump();
    });
  }
}

// ── Direct-upload driver (transport B) ───────────────────────────────────────────────────────────────

function directRetryTransport(operation: LocalUploadRetryOperation) {
  return operation === 'upload_chunk'
    ? ('gcs_resumable_xhr' as const)
    : ('vision_lab_api' as const);
}

function runDirectRetryableOperation<T>(
  id: string,
  operation: LocalUploadRetryOperation,
  signal: AbortSignal,
  run: () => Promise<T>,
): Promise<T> {
  return runRetryableUploadOperation({
    operation,
    transport: directRetryTransport(operation),
    run,
    signal,
    onRetry: (event) => recordAutomaticRetry(id, event),
  }).then((value) => {
    settleAutomaticRetry(id);
    return value;
  });
}

async function reportVideoProgressWithRetry(
  id: string,
  sessionId: string,
  partNo: number,
  cumulativeBytes: number,
  signal: AbortSignal,
): Promise<Submission> {
  for (let retries = 0; ; retries += 1) {
    try {
      const reported = await reportProgress(sessionId, partNo, cumulativeBytes);
      settleAutomaticRetry(id);
      return reported;
    } catch (error) {
      if (!isRetryableUploadError(error)) throw error;
      const latest = await runDirectRetryableOperation(id, 'report_progress', signal, () =>
        getSubmission(sessionId),
      );
      if (latest.status === 'failed') {
        throw new Error(latest.failReason ?? 'upload_session_failed', { cause: error });
      }
      if (latest.receivedBytes === cumulativeBytes && latest.nextPart === partNo + 1) {
        settleAutomaticRetry(id);
        return latest;
      }
      if (
        latest.status !== 'uploading' ||
        latest.nextPart !== partNo ||
        latest.receivedBytes > cumulativeBytes
      ) {
        throw new Error('upload_progress_conflict', { cause: error });
      }
      if (retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      await waitForLocalUploadRetry({
        operation: 'report_progress',
        transport: 'vision_lab_api',
        attempt: retries + 1,
        error,
        signal,
        onRetry: (event) => recordAutomaticRetry(id, event),
      });
    }
  }
}

async function putVideoChunkWithRetry({
  id,
  sessionId,
  uri,
  chunk,
  start,
  total,
  isFinal,
  signal,
  onProgress,
}: {
  id: string;
  sessionId: string;
  uri: string;
  chunk: Blob;
  start: number;
  total: number;
  isFinal: boolean;
  signal: AbortSignal;
  onProgress: (loaded: number) => void;
}): Promise<number> {
  for (let retries = 0; ; retries += 1) {
    try {
      const status = await putChunk(id, uri, chunk, start, total, isFinal, onProgress);
      if (status === 401 && uploadActions.get(id)?.uploadRoute === 'china' && retries === 0) {
        entries.get(id)?.relay?.invalidate();
        continue;
      }
      const accepted = isFinal
        ? status === 200 || status === 201
        : status === 308 || status === 200 || status === 201;
      if (!accepted) throw new UploadChunkHttpError(status, start, chunk.size, isFinal);
      settleAutomaticRetry(id);
      return start + chunk.size;
    } catch (error) {
      // Browser progress is not committed GCS progress. Drop the volatile sample before probing the
      // authoritative offset so a retry never keeps displaying bytes that GCS may not have stored.
      uploadProgressActions.clear(id);
      if (!isRetryableUploadError(error) || retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      const offset = await runDirectRetryableOperation(id, 'fetch_upload_offset', signal, () =>
        getUploadOffset(sessionId),
      );
      if (offset < start) throw new Error('upload_offset_regressed', { cause: error });
      if (offset > total) throw new Error('upload_offset_overflow', { cause: error });
      if (offset > start) {
        settleAutomaticRetry(id);
        return offset;
      }
      await waitForLocalUploadRetry({
        operation: 'upload_chunk',
        transport: 'gcs_resumable_xhr',
        attempt: retries + 1,
        error,
        signal,
        onRetry: (event) => recordAutomaticRetry(id, event),
      });
    }
  }
}

async function completeVideoWithRetry(
  id: string,
  sessionId: string,
  signal: AbortSignal,
): Promise<Submission> {
  for (let retries = 0; ; retries += 1) {
    try {
      const completed = await completeSubmission(sessionId);
      settleAutomaticRetry(id);
      return completed;
    } catch (error) {
      const isConflict = error instanceof ApiHttpError && error.status === 409;
      if (!isRetryableUploadError(error) && !isConflict) throw error;
      const latest = await runDirectRetryableOperation(id, 'complete_submission', signal, () =>
        getSubmission(sessionId),
      );
      if (latest.status === 'uploaded') {
        settleAutomaticRetry(id);
        return latest;
      }
      if (latest.status === 'failed') {
        throw new Error(latest.failReason ?? 'upload_session_failed', { cause: error });
      }
      if (!isRetryableUploadError(error)) throw error;
      if (retries >= LOCAL_UPLOAD_MAX_AUTO_RETRIES) throw error;
      await waitForLocalUploadRetry({
        operation: 'complete_submission',
        transport: 'vision_lab_api',
        attempt: retries + 1,
        error,
        signal,
        onRetry: (event) => recordAutomaticRetry(id, event),
      });
    }
  }
}

async function runUpload(id: string): Promise<void> {
  const e = entries.get(id);
  const item = uploadActions.get(id);
  if (!e || !e.file || !item || e.cancelled) return;
  const controller = new AbortController();
  e.retryController = controller;
  const signal = controller.signal;
  const file = e.file;
  const resumed = Boolean(item.backendId);
  let contentBlake3 = item.contentBlake3;
  try {
    // 1. The authenticated, project-authorized quality gate runs before any hash/PDQ dedup request.
    // A recovered new session already exists, but it still needs this gate before byte transfer.
    if (!item.backendId || e.needsQualityCheck) {
      set(id, { phase: 'prechecking', bytes: 0 });
      const evidence = await buildQualityEvidence(file);
      const quality = await runDirectRetryableOperation(id, 'quality_check', signal, () =>
        checkVideoQuality(item.target.projectId, evidence),
      );
      if (quality.status === 'rejected') {
        const { fps, bitrateKbps, durationSecs, pixels } = quality.facts;
        set(id, {
          phase: 'rejected-quality',
          reason: `Quality rejected (fps ${fps}, bitrate ${bitrateKbps} kbps, duration ${durationSecs}s, pixels ${pixels})`,
        });
        return;
      }
      e.needsQualityCheck = false;
    }

    if (!item.backendId) {
      // 2. Hash, exact-duplicate check and session creation for a fresh item.
      let blake3 = e.preparedContentBlake3;
      if (!blake3) {
        set(id, { phase: 'hashing', bytes: 0 });
        blake3 = await blake3File(file, (bytes) =>
          uploadProgressActions.report(id, {
            stage: 'verification',
            bytes,
            totalBytes: file.size,
          }),
        );
      }
      set(id, { contentBlake3: blake3, phase: 'prechecking', bytes: 0 });
      contentBlake3 = blake3;
    }

    // Old persisted rows may predate content-hash persistence. Derive the same backend uniqueness
    // identity before touching their resumable session so every tab still enters the same lock.
    if (!contentBlake3) {
      set(id, { phase: 'hashing', bytes: 0 });
      contentBlake3 = await blake3File(file, (bytes) =>
        uploadProgressActions.report(id, {
          stage: 'verification',
          bytes,
          totalBytes: file.size,
        }),
      );
      set(id, { contentBlake3, phase: 'prechecking', bytes: 0 });
    }
    const resourceHash = contentBlake3;
    if (!resourceHash) throw new Error('upload_resource_identity_unavailable');

    const uploaderId = getActiveUploadUid() ?? 'current-session';

    await withUploadResourceLock({ uploaderId, contentBlake3: resourceHash }, signal, async () => {
      // Waiting for another tab can take minutes. Re-read the store and backend only after this tab
      // owns the resource; a predecessor may already have created or completed the same session.
      const lockedItem = uploadActions.get(id);
      if (!lockedItem) return;
      if (!lockedItem.backendId) {
        const dedup = await checkContentHash(resourceHash);
        if (dedup === 'exists') {
          set(id, { phase: 'blocked-duplicate', reason: 'Already exists (identical content)' });
          return;
        }

        // 3. create session (resume on duplicate)
        const sessionId = await ensureSession(
          id,
          resourceHash,
          lockedItem.target.projectId,
          file.size,
          file.name,
          signal,
        );
        if (!sessionId) return; // blocked (e.g. failed prior session) — phase already set
        set(id, { backendId: sessionId });
      }

      const current = uploadActions.get(id);
      const sessionId = current?.backendId;
      if (!sessionId) return;

      // 2b. Run gate 2 whenever the persisted item has no completed advisory. This includes a session
      // reattached after reload: resuming must not silently skip a precheck merely because backendId is
      // already known. A recorded negative/inconclusive outcome is stable for this upload attempt.
      if (current.dedupPrecheckOutcome === 'nearDuplicate') {
        set(id, {
          phase: 'blocked-near-duplicate',
          reason: 'Near-duplicate of an existing video',
        });
        return;
      }
      if (!current.dedupPrecheckOutcome) {
        set(id, { phase: 'prechecking' });
        const gate = await runDedupPrecheck(id, sessionId, file);
        if (gate !== 'proceed') return;
      }

      // 3. resumable session + offset
      let session: Submission;
      try {
        session = await runDirectRetryableOperation(id, 'begin_resumable', signal, () =>
          resumableBegin(sessionId, file.type || 'application/octet-stream'),
        );
      } catch (error) {
        const missing = error instanceof ApiHttpError && error.status === 404;
        if (!missing || !current.contentBlake3) throw error;
        const recreated = await ensureSession(
          id,
          current.contentBlake3,
          current.target.projectId,
          file.size,
          file.name,
          signal,
          sessionId,
        );
        if (!recreated) return;
        session = await runDirectRetryableOperation(id, 'begin_resumable', signal, () =>
          resumableBegin(recreated, file.type || 'application/octet-stream'),
        );
      }
      if (session.status === 'uploaded') {
        set(id, {
          phase: 'processing',
          bytes: session.declaredBytes,
          totalBytes: session.declaredBytes,
          autoRetry: undefined,
        });
        const completed = uploadActions.get(id);
        if (completed) captureLocalUploadCompleted(completed, true);
        uploadStatusCoordinator.track(id, sessionId, 'video');
        return;
      }
      if (session.status === 'failed')
        throw new Error(session.failReason ?? 'upload_session_failed');
      const resumableUri = session.resumableUri;
      if (!resumableUri) {
        recordLocalUploadFailure(id, 'resumable_begin', new Error('resumable_uri_unavailable'));
        set(id, { phase: 'error', reason: 'Resumable upload not available (retry)' });
        return;
      }
      // Never guess this from the submission row: a tab can close after GCS commits a PUT but before
      // `report-progress` reaches the backend, which is precisely the divergence resume must repair.
      let offset = await runDirectRetryableOperation(id, 'fetch_upload_offset', signal, () =>
        getUploadOffset(sessionId),
      );

      // 4. Reconcile the two resume cursors, then continue the chunked PUT loop. GCS owns the committed
      // byte offset; the backend owns the zero-based progress-report sequence number.
      set(id, { phase: 'uploading', bytes: offset });
      const resume = planUploadResume({
        offset,
        fileSize: file.size,
        receivedBytes: session.receivedBytes,
        nextPart: session.nextPart,
      });
      let partNo = resume.partNo;
      if (resume.reconcileBytes !== undefined) {
        const reported = await reportVideoProgressWithRetry(
          id,
          sessionId,
          partNo,
          resume.reconcileBytes,
          signal,
        );
        partNo = reported.nextPart;
      }
      const startedAt = Date.now();
      while (offset < file.size) {
        if (e.paused || e.cancelled) return;
        const end = Math.min(offset + CHUNK_BYTES, file.size);
        const isFinal = end >= file.size;
        const chunk = file.slice(offset, end);
        const chunkStart = offset;
        const confirmed = await putVideoChunkWithRetry({
          id,
          sessionId,
          uri: resumableUri,
          chunk,
          start: chunkStart,
          total: file.size,
          isFinal,
          signal,
          onProgress: (loaded) => {
            const total = chunkStart + loaded;
            const secs = (Date.now() - startedAt) / 1000;
            uploadProgressActions.report(id, {
              stage: 'transfer',
              bytes: total,
              totalBytes: file.size,
              speedBps: secs > 0 ? total / secs : undefined,
            });
          },
        });
        if (e.cancelled) return;
        offset = confirmed;
        set(id, { bytes: offset });
        // The backend expects the zero-based part currently being acknowledged, then advances
        // `nextPart` itself. Do not hide a rejected progress update: continuing would leave the GCS
        // object ahead of the submission record and make the eventual completion failure opaque.
        const reported = await reportVideoProgressWithRetry(id, sessionId, partNo, offset, signal);
        partNo = reported.nextPart;
      }

      // 5. complete (server gcs_stat authoritative; ambiguous final commits were reconciled by offset)
      set(id, { phase: 'completing' });
      await completeVideoWithRetry(id, sessionId, signal);

      const completed = uploadActions.get(id);
      if (completed) captureLocalUploadCompleted(completed, resumed);

      // 6. hand off to the batch status coordinator, which reconciles intake to a terminal outcome
      set(id, { phase: 'processing', autoRetry: undefined });
      uploadStatusCoordinator.track(id, sessionId, 'video');
    });
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return; // paused/cancelled
    if (hasUploadHttpStatus(err, 410)) {
      expireUploadSession(id);
      return;
    }
    recordLocalUploadFailure(id, uploadActions.get(id)?.phase ?? item.phase, err);
    set(id, {
      phase: 'error',
      reason: describeError(err, 'Upload failed'),
      autoRetry: undefined,
    });
  } finally {
    if (e.retryController === controller) e.retryController = undefined;
  }
}

export type DedupPrecheckAction = 'block' | 'proceed';
export type DedupPrecheckErrorDisposition = 'retry' | 'stop';
type DedupPrecheckGateResult = DedupPrecheckAction | 'stop';

/** The backend outcome is already redacted and complete: only a positive match blocks. An
 * inconclusive advisory proceeds to the separate authoritative intake; it is never relabeled as a
 * no-match. */
export function dedupPrecheckAction(outcome: DedupPrecheckOutcome): DedupPrecheckAction {
  return outcome === 'nearDuplicate' ? 'block' : 'proceed';
}

/** Contract/session/auth failures cannot be converted into negative evidence. Transient transport,
 * rate-limit and server failures may be retried with a newly signed exact plan. */
export function dedupPrecheckErrorDisposition(error: unknown): DedupPrecheckErrorDisposition {
  if (error instanceof ApiHttpError) {
    return error.status === 429 || error.status >= 500 ? 'retry' : 'stop';
  }
  return 'retry';
}

const PRECHECK_STOP_REASONS: Record<number, string> = {
  400: 'PDQ precheck request was rejected by the backend contract',
  401: 'Sign in again before retrying the duplicate check',
  403: 'You cannot run the duplicate check for this project',
  404: 'The upload session is no longer available',
  409: 'The upload session is already complete or failed',
};

function precheckStopReason(error: unknown): string {
  if (error instanceof ApiHttpError) {
    return PRECHECK_STOP_REASONS[error.status] ?? `PDQ precheck failed (status ${error.status})`;
  }
  return 'PDQ precheck failed';
}

const PRECHECK_ATTEMPTS = 2;

/** Execute the exact backend PR #71 protocol. Local evidence below the backend's minimum is typed as
 * inconclusive without issuing an invalid request. Transient failures receive one new signed-plan
 * attempt; exhaustion remains inconclusive and hands authority to post-upload intake. */
async function runDedupPrecheck(
  id: string,
  sessionId: string,
  file: File,
): Promise<DedupPrecheckGateResult> {
  const extracted = await extractPdqFrames(file);
  const frames = extracted.slice(0, MAX_DEDUP_FRAME_COUNT);
  if (frames.length < MIN_DEDUP_FRAME_COUNT) {
    set(id, { dedupPrecheckOutcome: 'inconclusive' });
    console.info('[uploads] PDQ precheck inconclusive: fewer than three complete sample frames');
    return 'proceed';
  }

  for (let attempt = 1; attempt <= PRECHECK_ATTEMPTS; attempt += 1) {
    try {
      if (uploadActions.get(id)?.uploadRoute === 'china') {
        await uploadRelayFrames(
          sessionId,
          frames.map((frame) => frame.blob),
          entries.get(id)?.retryController?.signal,
        );
      } else {
        const { frames: urls } = await requestDedupFrameUrls(sessionId, frames.length);
        await Promise.all(
          urls.map((entry) => putSignedFrame(entry.uploadUrl, frames[entry.index].blob)),
        );
      }

      const { outcome } = await checkNearDuplicate(sessionId, frames.length);
      set(id, { dedupPrecheckOutcome: outcome });
      const action = dedupPrecheckAction(outcome);
      if (action === 'block') {
        set(id, {
          phase: 'blocked-near-duplicate',
          reason: 'Near-duplicate of an existing video',
        });
      }
      return action;
    } catch (error) {
      if (dedupPrecheckErrorDisposition(error) === 'stop') {
        recordLocalUploadFailure(id, 'prechecking', error);
        set(id, { phase: 'error', reason: precheckStopReason(error) });
        return 'stop';
      }
      if (attempt === PRECHECK_ATTEMPTS) {
        set(id, { dedupPrecheckOutcome: 'inconclusive' });
        console.warn(
          '[uploads] PDQ precheck inconclusive after transient retry; intake remains authoritative',
          error,
        );
        return 'proceed';
      }
    }
  }
  return 'stop';
}

export type SubmissionCreateErrorDisposition = 'duplicate' | 'forbidden' | 'error';

/** A new queue item must never adopt an existing submission. Browser-close recovery already carries
 * its persisted backendId and enters through `reattachFile`; a 409 here therefore means this is a
 * second selection of content that already has a submission. */
export function submissionCreateErrorDisposition(error: unknown): SubmissionCreateErrorDisposition {
  if (isForbidden(error)) return 'forbidden';
  if (error instanceof ApiHttpError && error.status === 409) return 'duplicate';
  return 'error';
}

/** Create a fresh submission session. Duplicate content is terminal for this new queue item; only a
 * persisted item with its original backendId may resume an existing session. */
async function ensureSession(
  id: string,
  blake3: string,
  projectId: string,
  declaredBytes: number,
  originalFilename: string,
  signal: AbortSignal,
  preferredSessionId?: string,
): Promise<string | undefined> {
  set(id, { phase: 'creating' });
  const sessionId = preferredSessionId ?? mintSessionId(blake3);
  const body = {
    sessionId,
    projectId,
    contentBlake3: blake3,
    mediaType: 'video' as const,
    originalFilename,
    declaredBytes,
  };
  set(id, { backendId: sessionId });
  for (let retries = 0; ; retries += 1) {
    try {
      const outcome = await createSubmission(body);
      if (outcome.kind === 'created') return sessionId;

      if (outcome.kind === 'duplicate-asset') {
        const projectName = duplicateAssetProjectName(outcome);
        set(id, {
          backendId: undefined,
          phase: 'blocked-duplicate',
          reason: projectName
            ? `Already exists in project “${projectName}”`
            : 'This file already exists',
          autoRetry: undefined,
        });
        return undefined;
      }

      const existing = await runDirectRetryableOperation(id, 'resolve_submission', signal, () =>
        getSubmission(outcome.sessionId),
      );
      const matches =
        existing.projectId === projectId &&
        existing.contentBlake3 === blake3 &&
        existing.declaredBytes === declaredBytes &&
        existing.mediaType === 'video';
      if (!matches) {
        set(id, {
          backendId: undefined,
          phase: 'blocked-duplicate',
          reason: 'A submission for this file already exists',
          autoRetry: undefined,
        });
        return undefined;
      }
      if (existing.status === 'uploaded') {
        set(id, {
          backendId: existing.sessionId,
          phase: 'processing',
          bytes: existing.declaredBytes,
          totalBytes: existing.declaredBytes,
          autoRetry: undefined,
        });
        uploadStatusCoordinator.track(id, existing.sessionId, 'video');
        return undefined;
      }
      if (existing.status === 'failed') {
        set(id, {
          backendId: existing.sessionId,
          phase: 'failed',
          reason: existing.failReason ?? 'This upload session has failed',
          autoRetry: undefined,
        });
        return undefined;
      }
      const entry = entries.get(id);
      if (entry) entry.paused = true;
      set(id, {
        backendId: existing.sessionId,
        contentBlake3: blake3,
        phase: 'paused',
        bytes: existing.receivedBytes,
        totalBytes: existing.declaredBytes,
        resumeAvailable: true,
        reason: 'Continue the existing upload',
        autoRetry: undefined,
      });
      return undefined;
    } catch (err) {
      let existing: Submission | undefined;
      if (isRetryableUploadError(err)) {
        try {
          existing = await runDirectRetryableOperation(id, 'resolve_submission', signal, () =>
            getSubmission(sessionId),
          );
        } catch (lookupError) {
          if (!(lookupError instanceof ApiHttpError && lookupError.status === 404))
            throw lookupError;
        }
      }
      if (existing) {
        const matches =
          existing.projectId === projectId &&
          existing.contentBlake3 === blake3 &&
          existing.declaredBytes === declaredBytes &&
          existing.mediaType === 'video';
        if (!matches) throw new Error('resume_session_mismatch', { cause: err });
        return sessionId;
      }

      if (isRetryableUploadError(err) && retries < LOCAL_UPLOAD_MAX_AUTO_RETRIES) {
        await waitForLocalUploadRetry({
          operation: 'resolve_submission',
          transport: 'vision_lab_api',
          attempt: retries + 1,
          error: err,
          signal,
          onRetry: (event) => recordAutomaticRetry(id, event),
        });
        continue;
      }

      const disposition = submissionCreateErrorDisposition(err);
      if (disposition === 'forbidden') {
        recordLocalUploadFailure(id, 'creating', err);
        set(id, {
          backendId: undefined,
          phase: 'error',
          reason: 'You cannot upload to this project',
          autoRetry: undefined,
        });
      } else if (disposition === 'duplicate') {
        set(id, {
          backendId: undefined,
          phase: 'blocked-duplicate',
          reason: 'A submission for this file already exists',
          autoRetry: undefined,
        });
      } else {
        recordLocalUploadFailure(id, 'creating', err);
        set(id, {
          backendId: undefined,
          phase: 'error',
          reason: describeError(err, 'Could not start upload'),
          autoRetry: undefined,
        });
      }
      return undefined;
    }
  }
}

// ── Manual MCAP recheck (command; the coordinator reconciles the result) ─────────────────────────────

/** Ask the backend to inspect the already-uploaded MCAP object again. This advances the business
 * generation and creates a new immutable scanner task; it never uploads the local file again. */
export async function retryMcapIngest(id: string): Promise<void> {
  if (!isLocalUploadLeader()) return;
  const item = uploadActions.get(id);
  if (
    !item?.backendId ||
    item.channel !== 'upload' ||
    item.mediaType !== 'mcap' ||
    item.phase !== 'failed' ||
    !item.mcapTaskId ||
    item.mcapCheckGeneration === undefined ||
    item.mcapRecheckable !== true
  ) {
    return;
  }

  const requestId = item.mcapRecheckRequestId ?? `retry-${crypto.randomUUID()}`;
  if (!entries.has(id)) entries.set(id, { paused: false, cancelled: false });
  set(id, {
    phase: 'processing',
    reason: undefined,
    mcapRecheckRequestId: requestId,
    mcapRecheckable: false,
  });

  try {
    const response = await requestMcapRecheck(
      item.backendId,
      requestId,
      item.mcapTaskId,
      'user requested MCAP recheck',
    );
    if (response.sessionId !== item.backendId || response.oldTaskId !== item.mcapTaskId) {
      throw new Error('MCAP recheck identity mismatch');
    }
    const nextGeneration = item.mcapCheckGeneration + 1;
    set(id, {
      phase: 'processing',
      mcapTaskId: response.newTaskId,
      mcapCheckGeneration: nextGeneration,
      mcapRecheckable: false,
      mcapRecheckRequestId: undefined,
      attemptLabel: `${nextGeneration}/3`,
    });
    uploadStatusCoordinator.track(id, item.backendId, 'mcap');
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 409) {
      // A concurrent recheck already advanced the scanner; let the coordinator reconcile the winner.
      uploadStatusCoordinator.track(id, item.backendId, 'mcap');
      return;
    }
    set(id, {
      phase: 'failed',
      reason: describeError(error, 'Could not request MCAP recheck'),
      mcapRecheckRequestId: requestId,
      mcapRecheckable: item.mcapRecheckable,
    });
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────────

class UploadChunkHttpError extends Error {
  readonly transport = 'gcs_resumable_xhr';

  constructor(
    readonly status: number,
    readonly chunkStartBytes: number,
    readonly chunkSizeBytes: number,
    readonly finalChunk: boolean,
  ) {
    super('upload_chunk_failed');
    this.name = 'UploadChunkHttpError';
  }
}

async function putChunk(
  id: string,
  uri: string,
  chunk: Blob,
  start: number,
  total: number,
  isFinal: boolean,
  onProgress: (loaded: number) => void,
): Promise<number> {
  const entry = entries.get(id);
  const task = uploadActions.get(id);
  let destination: { url: string; headers?: Record<string, string> } = { url: uri };
  if (task?.uploadRoute === 'china') {
    if (!entry || !task.backendId) throw new Error('upload_relay_unavailable');
    entry.relay ??= createUploadRelayLease('china');
    destination = await entry.relay.destination(task.backendId, uri, entry.retryController?.signal);
    if (entry.paused || entry.cancelled || entry.retryController?.signal.aborted)
      throw new DOMException('aborted', 'AbortError');
  }
  return putGoogleStorageChunk({
    uploadUrl: destination.url,
    headers: destination.headers,
    chunk,
    start,
    totalBytes: total,
    finalChunk: isFinal,
    onProgress,
    onRequest: (request) => {
      if (entry) entry.xhr = request;
    },
    onSettled: (request) => {
      if (entry?.xhr === request) entry.xhr = undefined;
    },
  });
}

function describeError(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function currentReconcileItem(snapshot: UploadTask, generation: number): UploadTask | undefined {
  if (generation !== runtimeGeneration) return undefined;
  const current = uploadActions.get(snapshot.id);
  return current && current.backendId === snapshot.backendId && current.phase === snapshot.phase
    ? current
    : undefined;
}

async function reconcileUpload(snapshot: UploadTask, generation: number): Promise<void> {
  if (!snapshot.backendId || !currentReconcileItem(snapshot, generation)) return;
  try {
    const submission = await getSubmission(snapshot.backendId);
    if (!currentReconcileItem(snapshot, generation)) return;
    if (submission.status === 'failed') {
      set(snapshot.id, {
        phase: 'failed',
        reason: submission.failReason ?? 'Upload session failed',
        statusSyncError: undefined,
        needsReattach: undefined,
        needsRestart: undefined,
      });
    } else if (submission.status === 'abandoned') {
      const replacementId = submission.replacedBySessionId;
      if (!replacementId) {
        set(snapshot.id, {
          phase: 'failed',
          reason: 'Upload replacement linkage is unavailable',
          needsRestart: undefined,
          needsReattach: undefined,
        });
        return;
      }
      const replacement = await getSubmission(replacementId);
      if (!currentReconcileItem(snapshot, generation)) return;
      if (
        replacement.sessionId !== replacementId ||
        replacement.projectId !== submission.projectId ||
        replacement.contentBlake3 !== submission.contentBlake3 ||
        replacement.declaredBytes !== submission.declaredBytes ||
        replacement.mediaType !== submission.mediaType
      ) {
        set(snapshot.id, {
          phase: 'failed',
          reason: 'Upload replacement linkage is unavailable',
          needsRestart: undefined,
          needsReattach: undefined,
        });
        return;
      }
      set(snapshot.id, {
        backendId: replacementId,
        phase:
          replacement.status === 'uploaded'
            ? 'processing'
            : replacement.status === 'failed'
              ? 'failed'
              : 'paused',
        reason:
          replacement.status === 'failed' ? (replacement.failReason ?? 'Upload failed') : undefined,
        statusSyncError: undefined,
        bytes: replacement.receivedBytes,
        totalBytes: replacement.declaredBytes,
        needsRestart: undefined,
        needsReattach:
          replacement.status === 'pending' || replacement.status === 'uploading' ? true : undefined,
      });
      if (replacement.status === 'uploaded') {
        uploadStatusCoordinator.track(snapshot.id, replacementId, snapshot.mediaType);
      }
    } else if (submission.status === 'uploaded') {
      set(snapshot.id, {
        phase: 'processing',
        reason: undefined,
        statusSyncError: undefined,
        bytes: submission.declaredBytes,
        totalBytes: submission.declaredBytes,
        needsReattach: undefined,
        needsRestart: undefined,
      });
      uploadStatusCoordinator.track(snapshot.id, snapshot.backendId, snapshot.mediaType);
    } else if (snapshot.phase === 'completing') {
      const entry = entries.get(snapshot.id);
      if (!entry) return;
      const controller = new AbortController();
      entry.retryController = controller;
      try {
        const offset = await runDirectRetryableOperation(
          snapshot.id,
          'fetch_upload_offset',
          controller.signal,
          () => getUploadOffset(snapshot.backendId as string),
        );
        if (!currentReconcileItem(snapshot, generation)) return;
        if (offset > submission.declaredBytes) throw new Error('upload_offset_overflow');
        if (offset < submission.declaredBytes) {
          set(snapshot.id, {
            phase: 'paused',
            bytes: offset,
            totalBytes: submission.declaredBytes,
            statusSyncError: undefined,
            needsReattach: true,
            resumeAvailable: undefined,
            reason: undefined,
          });
          return;
        }
        await completeVideoWithRetry(snapshot.id, snapshot.backendId, controller.signal);
        if (!currentReconcileItem(snapshot, generation)) return;
        set(snapshot.id, {
          phase: 'processing',
          bytes: submission.declaredBytes,
          totalBytes: submission.declaredBytes,
          statusSyncError: undefined,
          needsReattach: undefined,
          resumeAvailable: undefined,
          reason: undefined,
          autoRetry: undefined,
        });
        uploadStatusCoordinator.track(snapshot.id, snapshot.backendId, snapshot.mediaType);
      } finally {
        if (entry.retryController === controller) entry.retryController = undefined;
      }
    } else {
      set(snapshot.id, {
        phase: submission.status === 'uploading' ? 'paused' : snapshot.phase,
        statusSyncError: undefined,
        bytes: submission.receivedBytes,
        totalBytes: submission.declaredBytes,
        needsReattach: true,
        reason: undefined,
      });
    }
  } catch (error) {
    if (!currentReconcileItem(snapshot, generation)) return;
    if (isExpiredUploadSessionError(error)) {
      markUploadSessionExpired(snapshot.id);
      return;
    }
    set(snapshot.id, {
      reason: describeError(error, 'Could not reconcile upload status'),
      needsReattach:
        snapshot.phase === 'completing' ||
        snapshot.phase === 'processing' ||
        snapshot.statusSyncError
          ? undefined
          : true,
    });
  }
}

async function runReconciliation(generation: number): Promise<void> {
  const uploadCandidates: UploadTask[] = [];
  for (const item of uploadActions.all()) {
    if (generation !== runtimeGeneration) return;
    if (!item.backendId || isTerminalPhase(item.phase) || entries.has(item.id)) continue;
    entries.set(item.id, { paused: false, cancelled: false });
    if (item.channel === 'import') {
      uploadStatusCoordinator.track(item.id, item.backendId, item.mediaType);
    } else {
      uploadCandidates.push(item);
    }
  }

  let cursor = 0;
  const worker = async () => {
    for (;;) {
      if (generation !== runtimeGeneration) return;
      const item = uploadCandidates[cursor];
      cursor += 1;
      if (!item) return;
      await reconcileUpload(item, generation);
    }
  };
  const workerCount = Math.min(UPLOAD_RECONCILE_CONCURRENCY, uploadCandidates.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}

/** Reconcile persisted queue items after the authenticated user's UID-scoped store hydrates. */
export function reconcileUploads(): Promise<void> {
  const generation = runtimeGeneration;
  if (reconcileRun?.generation === generation) return reconcileRun.promise;
  const promise = runReconciliation(generation);
  const run = { generation, promise };
  reconcileRun = run;
  const clearRun = () => {
    if (reconcileRun === run) reconcileRun = null;
  };
  void promise.then(clearRun, clearRun);
  return promise;
}
